// strategy/resolve.ts - recommend(ctx): THE HEART's resolver (ENGINE_DESIGN.md §6.2).
//
//   baseline → filter rules whose `when` matches → sort by priority (desc, id-tiebroken for
//   determinism) → apply effects in order → emit RecommendedAction with firedRules[] and BOTH the
//   baseline and final action kept, so the coach can teach the deviation.
//
// Deterministic conflict semantics (locked, §6.2, lint-enforced §10.4):
//   • Higher priority wins. Equal priority is broken by rule id (ascending) so a run is reproducible.
//   • `forbid` accumulates and is never lifted - "later effects cannot un-forbid".
//   • `forbid` beats `set` at equal priority: a `set X` is only honored if X is not in the forbidden
//     set (which higher- AND equal-priority forbids have already populated when we reach the set).
//   • If the intended action ends up forbidden, de-escalate through a fixed legal fallback ladder.
//   • Sizing: highest-priority `set` picks the base intent; then floors raise it and ceils cap it.
//   • Threshold/bluff biases (valueThreshold / callThreshold / bluffFreq) only nudge the action when
//     NO rule set it explicitly - an explicit `set`/`forbid` always wins over a soft bias.
import { handClassRank, SIZING_FRACTION, } from '@postflop/schema';
import { baseline } from './baseline';
import { ruleMatches } from './match';
/** Sizing-intent ladder, smallest → largest; index is the ordinal used for floor/ceil clamping.
 *  Mirrors the SizingIntentSchema enum order (action.ts). 'min' and 'jam' bracket the ladder. */
const SIZING_LADDER = [
    'min', 'third', 'half', 'twoThirds', 'threeQuarters', 'pot', 'overbet', 'jam',
];
const sizingOrd = (s) => SIZING_LADDER.indexOf(s);
/** Betting actions that carry a sizing intent. */
const isBetting = (a) => a === 'bet' || a === 'raise' || a === 'jam';
/** Sort matched rules for deterministic application: priority desc, then id asc. */
export const orderRules = (rules) => [...rules].sort((a, b) => (b.priority - a.priority) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
export const toFiredRule = (r) => ({
    id: r.id,
    principle: r.explain.principle,
    villainNote: r.explain.villainNote,
    confidence: r.confidence,
    framework: r.source.framework,
});
/**
 * Collect the effects of an ORDERED (priority desc, id asc) run of matched rules under the locked
 * conflict semantics (§6.2): forbid accumulates; the FIRST value seen for a single-valued field
 * (setAction, each bias direction) is the highest-priority one and wins; floors take the strictest
 * (largest) lower bound and ceils the strictest (smallest) upper bound. Extracted from recommend()
 * so recommendPreflop() (PF1) composes preflop rules exactly as postflop rules compose.
 */
export const collectEffects = (matched) => {
    const forbidden = new Set();
    let setAction;
    let setSizing;
    let floor;
    let ceil;
    const biases = {};
    for (const r of matched) {
        const e = r.effect;
        if (e.action?.forbid)
            for (const a of e.action.forbid)
                forbidden.add(a);
        if (e.action?.set && setAction === undefined)
            setAction = { value: e.action.set, priority: r.priority };
        if (e.sizingIntent?.set && setSizing === undefined)
            setSizing = { value: e.sizingIntent.set, priority: r.priority };
        if (e.sizingIntent?.floor && (floor === undefined || sizingOrd(e.sizingIntent.floor) > sizingOrd(floor.value))) {
            floor = { value: e.sizingIntent.floor, priority: r.priority };
        }
        if (e.sizingIntent?.ceil && (ceil === undefined || sizingOrd(e.sizingIntent.ceil) < sizingOrd(ceil.value))) {
            ceil = { value: e.sizingIntent.ceil, priority: r.priority };
        }
        if (e.callThreshold && biases.callThreshold === undefined)
            biases.callThreshold = e.callThreshold;
        if (e.valueThreshold && biases.valueThreshold === undefined)
            biases.valueThreshold = e.valueThreshold;
        if (e.bluffFreq && biases.bluffFreq === undefined)
            biases.bluffFreq = e.bluffFreq;
    }
    return { forbidden, setAction, setSizing, floor, ceil, biases };
};
/** Fixed de-escalation ladder used when the intended action is forbidden. Facing a bet we step down
 *  toward folding; not facing a bet we step down toward checking. First non-forbidden wins. */
const fallbackAction = (ctx, forbidden) => {
    const ladder = ctx.price.toCall > 0 ? ['call', 'fold', 'raise', 'jam'] : ['check', 'bet', 'jam'];
    for (const a of ladder)
        if (!forbidden.has(a))
            return a;
    return ctx.price.toCall > 0 ? 'fold' : 'check';
};
/** Apply the soft threshold/bluff biases to the baseline action when no rule set the action.
 *  Conservative and deterministic: each bias only fires in the one spot where its meaning is clear. */
const applyBiases = (base, ctx, biases) => {
    const r = handClassRank(ctx.hero.handClass);
    const facingBet = ctx.price.toCall > 0;
    let action = base.action;
    if (facingBet) {
        // widen bluff-catchers: a fold with real showdown value becomes a call.
        if (biases.callThreshold === 'wider' && action === 'fold' && r >= handClassRank('weak_pair')) {
            action = 'call';
        }
        // tighten call-downs: a thin one-pair call is released.
        if (biases.callThreshold === 'tighter' &&
            action === 'call' &&
            r <= handClassRank('overpair')) {
            action = 'fold';
        }
    }
    else {
        // thin value: a checked marginal made hand starts betting.
        if (biases.valueThreshold === 'thinner' &&
            action === 'check' &&
            r >= handClassRank('weak_pair') &&
            r <= handClassRank('top_pair_weak_kicker')) {
            action = 'bet';
        }
        // stricter value: a thin value bet checks back instead.
        if (biases.valueThreshold === 'stricter' &&
            action === 'bet' &&
            r < handClassRank('two_pair')) {
            action = 'check';
        }
        // read-driven bluff: attack when an over-folder / attackable spot is flagged.
        if (biases.bluffFreq === 'increase' && action === 'check' && r <= handClassRank('strong_draw')) {
            action = 'bet';
        }
        // kill bluffs: give up instead of betting air.
        if ((biases.bluffFreq === 'zero' || biases.bluffFreq === 'reduce') &&
            action === 'bet' &&
            r <= handClassRank('strong_draw')) {
            action = 'check';
        }
    }
    return action;
};
/** Coarse EV-gap bucket for an alternative, from its rank distance below the chosen action. The
 *  precise per-bb EV loss (evLossBB) is the grading engine's job (§7.2, S8); here we only need a
 *  deterministic ordering so the coach can list "what else you could do". */
const gapByRank = (rankBelowChosen) => rankBelowChosen <= 0 ? 'negligible' : rankBelowChosen === 1 ? 'small' : rankBelowChosen === 2 ? 'clear' : 'severe';
/** Resolve the final sizing intent for a betting action from baseline + rule set/floor/ceil. */
const resolveSizing = (finalAction, base, setSizing, floor, ceil) => {
    if (!isBetting(finalAction))
        return undefined;
    if (finalAction === 'jam')
        return 'jam';
    // start from an explicit rule set, else the baseline sizing for this action, else a sane default.
    const start = setSizing?.value ??
        (base.action === finalAction ? base.sizingIntent : undefined) ??
        (finalAction === 'raise' ? 'threeQuarters' : 'twoThirds');
    let ord = sizingOrd(start);
    const floorOrd = floor ? sizingOrd(floor.value) : undefined;
    const ceilOrd = ceil ? sizingOrd(ceil.value) : undefined;
    // floor raises, ceil caps. If they conflict (floor > ceil), the higher-priority bound wins.
    if (floorOrd !== undefined && ord < floorOrd)
        ord = floorOrd;
    if (ceilOrd !== undefined && ord > ceilOrd)
        ord = ceilOrd;
    if (floorOrd !== undefined && ceilOrd !== undefined && floorOrd > ceilOrd) {
        ord = (floor.priority >= ceil.priority) ? floorOrd : ceilOrd;
    }
    return SIZING_LADDER[ord];
};
/**
 * The resolver. Runs the baseline, fires the matching rules, applies their effects under the locked
 * conflict semantics, and returns the full RecommendedAction (baseline + final + firedRules +
 * alternatives). Pure function of `ctx` - same context, same recommendation, every time.
 */
export const recommend = (ctx) => {
    const base = baseline(ctx);
    const matched = orderRules(ctx.rules.filter((r) => ruleMatches(r, ctx)));
    const { forbidden, setAction, setSizing, floor, ceil, biases } = collectEffects(matched);
    // Choose the interim action: an honored explicit set beats everything; otherwise the baseline,
    // possibly nudged by soft biases. Then de-escalate if the choice is forbidden.
    let interim;
    if (setAction && !forbidden.has(setAction.value)) {
        interim = setAction.value;
    }
    else {
        interim = applyBiases(base, ctx, biases);
    }
    const finalAction = forbidden.has(interim) ? fallbackAction(ctx, forbidden) : interim;
    const sizingIntent = resolveSizing(finalAction, base, setSizing, floor, ceil);
    // alternatives: the baseline's ranked candidates minus the final action, plus the final action's
    // baseline position if it displaced ranked[0]. Coarse EV-gap by rank distance (real EV = S8).
    const candidates = [...base.ranked];
    const alternatives = candidates
        .filter((c) => c.action !== finalAction)
        .map((c, i) => ({ action: c.action, sizingIntent: c.sizingIntent, evGapBucket: gapByRank(i + 1) }));
    const targetStatement = describeTarget(finalAction, base, sizingIntent);
    return {
        baselineAction: base.action,
        finalAction,
        action: finalAction,
        sizingIntent,
        targetStatement,
        firedRules: matched.map((r) => ruleShapedAnswer(r, finalAction) ? toFiredRule(r) : { ...toFiredRule(r), suppressed: true }),
        alternatives,
    };
};
/**
 * Did this matched rule's effect actually SHAPE the answer the engine gave? (The postflop mirror of
 * strategy/preflop.ts ruleShapedAnswer - PF7 fills the paid lane's half of the same hole PF6 proved.)
 *
 * Matching is not the same as mattering. A rule matches on the SPOT (villain archetype, texture, SPR)
 * without ever seeing hero's cards, and collectEffects/recommend can then DECLINE its effect: an explicit
 * `action.set` is dropped when a co-matched forbid outranks it (`if (setAction && !forbidden.has(...))`)
 * or when a higher-priority set already claimed the single setAction slot, and a soft value/bluff/call
 * bias only nudges the one narrow spot applyBiases fires it in. When that happens the rule's principle
 * argues for a line the engine did NOT take, and handing that prose to the coach is how a debrief ends up
 * saying "get the chips in" and "just call" in one breath - at a real per-call cost on the /api/coach lane.
 *
 * A rule stays in `firedRules` either way - that it matched is real provenance the yardstick and the
 * dimension attribution assert on. This only answers whether its PROSE explains the answer.
 */
const ruleShapedAnswer = (r, finalAction) => {
    const e = r.effect;
    // An explicit action.set shaped the answer only if the engine actually took that action.
    if (e.action?.set)
        return e.action.set === finalAction;
    // Everything below argues for committing chips (or more of them). When the engine's answer puts no new
    // money in this street - a fold, or a check-through - a sizing floor/ceil or a thin-value / attack /
    // bluff-catch-wider bias explains nothing about that passive line. (The TIGHTENING biases - stricter
    // value, reduce/zero bluff, tighter call-downs - DO explain a fold or a check, so they are never dropped:
    // applyBiases uses them to turn a bet into a check or a call into a fold.)
    if (finalAction === 'fold' || finalAction === 'check') {
        if (e.sizingIntent)
            return false;
        if (e.valueThreshold === 'thinner')
            return false;
        if (e.bluffFreq === 'increase')
            return false;
        if (e.callThreshold === 'wider')
            return false;
    }
    return true;
};
/** A short, honest statement of what the recommended line is doing. Prefers the baseline's own
 *  statement when the rule layer did not change the action; otherwise names the deviation plainly. */
const describeTarget = (finalAction, base, sizingIntent) => {
    if (finalAction === base.action)
        return base.targetStatement;
    const size = sizingIntent && SIZING_FRACTION[sizingIntent];
    switch (finalAction) {
        case 'bet':
            return size
                ? `read-driven bet (~${Math.round(size * 100)}% pot), deviating from the ${base.action} baseline`
                : `read-driven bet, deviating from the ${base.action} baseline`;
        case 'raise':
            return `raise: the read turns the ${base.action} baseline into pressure`;
        case 'call':
            return `call: the read widens past the ${base.action} baseline`;
        case 'jam':
            return `jam: the spot commits, overriding the ${base.action} baseline`;
        case 'fold':
            return `fold: the read tightens past the ${base.action} baseline`;
        case 'check':
            return `check: the read pulls back from the ${base.action} baseline`;
        default:
            return base.targetStatement;
    }
};
//# sourceMappingURL=resolve.js.map